import type { KeyboardLayoutObject, SimpleKeyboard } from 'react-simple-keyboard';
import { getWritableInputTypesConfig, getLayoutsByLanguage } from '../utils/KeyUtils';
import { KeyboardLayoutType, KeyboardModule, KeyboardOptionsExt } from 'components/Keyboard/types';
import { SPECIAL_KEYS } from '../utils/constants';

const DEFAULT_LAYOUT_INDEX = 0;

const withNextKeyboard = (rows: string[]) => [
  ...rows.slice(0, -1),
  `${SPECIAL_KEYS.NEXT_KEYBOARD} ${rows[rows.length - 1]}`,
];

const prepareKeyboardLayout = (layout: KeyboardLayoutObject) => ({
  ...layout,
  default: withNextKeyboard(layout.default),
  shift: withNextKeyboard(layout.shift),
  ...(layout.special && { special: withNextKeyboard(layout.special) }),
});

function multiKeyboards(keyboard: SimpleKeyboard) {
  keyboard.registerModule('multiKeyboards', (module: KeyboardModule) => {
    const { type, language, layoutType } = keyboard.getOptions() as KeyboardOptionsExt;
    if (!getWritableInputTypesConfig(language)[type]?.layout) {
      const layouts = getLayoutsByLanguage(language);
      module.index = layoutType === KeyboardLayoutType.ALTERNATE ? layouts.length - 1 : DEFAULT_LAYOUT_INDEX;
      module.fn = { handleButtonClicked: keyboard.handleButtonClicked };

      const defaultKeyboard = layouts.at(module.index);
      const defaultLayout = defaultKeyboard.layout;

      keyboard.setOptions({
        layoutName: 'default',
        keyboardName: defaultKeyboard?.name,
        layout: layouts.length > 1 ? prepareKeyboardLayout(defaultLayout) : defaultLayout,
      });

      keyboard.handleButtonClicked = (...args) => {
        const [button] = args;
        if (button === SPECIAL_KEYS.NEXT_KEYBOARD) {
          const nextIndex = (module.index + 1) % layouts.length;
          module.index = nextIndex;
          const newKeyboard = layouts.at(nextIndex);

          keyboard.setOptions({
            layoutName: 'default',
            keyboardName: newKeyboard?.name,
            layout: prepareKeyboardLayout(newKeyboard.layout),
          });
        }
        module.fn.handleButtonClicked.call(keyboard, ...args);
      };
    }
  });
}

export default multiKeyboards;
